export type DocRow = readonly [name: string, detail: string];

export interface CatalogEntry {
  tag: string;
  title: string;
  category: string;
  description: string;
  demo: string;
  example: string;
  properties: DocRow[];
  events: DocRow[];
  slots: DocRow[];
  parts: DocRow[];
}

const p = (...rows: DocRow[]) => rows;
const commonSize = p(['size', '`sm` | `md` | `lg`; invalid values become `md`']);
const change = p(['change', 'Bubbles and crosses the Shadow DOM boundary']);
const form = p(
  ['name', 'Submitted form field name'],
  ['value', 'Current submitted value'],
  ['disabled', 'Suppresses interaction and submission'],
  ['required', 'Participates in constraint validation'],
);
const entry = (
  value: Omit<CatalogEntry, 'properties' | 'events' | 'slots' | 'parts'> &
    Partial<Pick<CatalogEntry, 'properties' | 'events' | 'slots' | 'parts'>>,
): CatalogEntry => ({
  properties: [],
  events: [],
  slots: [],
  parts: [],
  ...value,
});

export const catalog: CatalogEntry[] = [
  entry({
    tag: 'ps-button',
    title: 'Button',
    category: 'Actions',
    description: 'A chunky action with four visual variants and native form intent.',
    demo: '<ps-button>Primary</ps-button> <ps-button variant="secondary">Secondary</ps-button> <ps-button variant="outline">Outline</ps-button> <ps-button variant="ghost">Ghost</ps-button>',
    example: '<ps-button variant="primary" size="lg">Start dancing</ps-button>',
    properties: [
      ...commonSize,
      ['variant', '`primary` | `secondary` | `outline` | `ghost`'],
      ['type', '`button` | `submit` | `reset`'],
      ['disabled', 'Suppresses interaction'],
    ],
    slots: p(['default', 'Label'], ['prefix', 'Leading icon'], ['suffix', 'Trailing icon']),
    parts: p(['button', 'Native button']),
  }),
  entry({
    tag: 'ps-icon-button',
    title: 'Icon Button',
    category: 'Actions',
    description: 'A round icon-only action with a required accessible label.',
    demo: '<ps-icon-button label="Add sparkle">✦</ps-icon-button> <ps-icon-button label="Favorite" variant="outline">♥</ps-icon-button>',
    example: '<ps-icon-button label="Favorite">♥</ps-icon-button>',
    properties: [
      ...commonSize,
      ['label', 'Accessible name'],
      ['variant', 'Button visual variant'],
      ['disabled', 'Suppresses interaction'],
    ],
    slots: p(['default', 'Icon']),
    parts: p(['button', 'Native button']),
  }),
  entry({
    tag: 'ps-button-group',
    title: 'Button Group',
    category: 'Actions',
    description: 'Keeps related actions together under one accessible group name.',
    demo: '<ps-button-group label="Playback"><ps-button size="sm">Play</ps-button><ps-button size="sm" variant="outline">Pause</ps-button></ps-button-group>',
    example: '<ps-button-group label="Actions">…</ps-button-group>',
    properties: p(['label', 'Accessible group name']),
    slots: p(['default', 'Buttons']),
    parts: p(['group', 'Group wrapper']),
  }),

  entry({
    tag: 'ps-field',
    title: 'Field',
    category: 'Forms',
    description: 'Combines labels, help, errors, and any control without hiding semantics.',
    demo: '<ps-field required><span slot="label">Mall nickname</span><ps-input value="Starlight"></ps-input><span slot="help">Shown on your arcade card.</span></ps-field>',
    example:
      '<ps-field required><span slot="label">Name</span><ps-input name="name" required></ps-input></ps-field>',
    properties: p(['required', 'Shows the required marker']),
    slots: p(
      ['label', 'Field label'],
      ['default', 'Control'],
      ['help', 'Help text'],
      ['error', 'Validation error'],
    ),
    parts: p(['label', 'Label'], ['help', 'Help'], ['error', 'Error']),
  }),
  entry({
    tag: 'ps-input',
    title: 'Input',
    category: 'Forms',
    description: 'A form-associated single-line control with prefix and suffix slots.',
    demo: '<ps-input name="search" type="search" placeholder="Search the mall" aria-label="Search the mall"><span slot="prefix">⌕</span></ps-input>',
    example:
      '<ps-input name="email" type="email" required placeholder="you@example.com"></ps-input>',
    properties: [
      ...form,
      ...commonSize,
      ['type', 'Text, email, password, search, URL, or telephone'],
      ['placeholder', 'Empty-state hint'],
    ],
    events: p(['input', 'Composed value update'], ...change),
    slots: p(['prefix', 'Leading content'], ['suffix', 'Trailing content']),
    parts: p(['base', 'Wrapper'], ['input', 'Native input']),
  }),
  entry({
    tag: 'ps-textarea',
    title: 'Textarea',
    category: 'Forms',
    description: 'A resizable, form-associated multiline text control.',
    demo: '<ps-textarea name="note" rows="3" placeholder="Leave a mixtape note…"></ps-textarea>',
    example: '<ps-textarea name="message" rows="5" required></ps-textarea>',
    properties: [
      ...form,
      ...commonSize,
      ['rows', 'Visible line count'],
      ['placeholder', 'Empty-state hint'],
    ],
    events: p(['input', 'Composed value update'], ...change),
    parts: p(['textarea', 'Native textarea']),
  }),
  entry({
    tag: 'ps-select',
    title: 'Select',
    category: 'Forms',
    description: 'A deliberately single-selection, form-associated picker for v0.1.0.',
    demo: '<ps-select name="store" value="records" aria-label="Favorite store"><ps-option value="records">Record store</ps-option><ps-option value="arcade">Arcade</ps-option><ps-option value="food">Food court</ps-option></ps-select>',
    example: '<ps-select name="flavor"><ps-option value="cherry">Cherry</ps-option></ps-select>',
    properties: [...form, ...commonSize, ['placeholder', 'Prompt option']],
    events: change,
    slots: p(['default', 'ps-option elements']),
    parts: p(['select', 'Native select']),
  }),
  entry({
    tag: 'ps-option',
    title: 'Option',
    category: 'Forms',
    description: 'The declarative option model consumed by ps-select.',
    demo: '<ps-select value="pink" aria-label="Color"><ps-option value="pink">Pink (ps-option)</ps-option><ps-option value="violet">Violet</ps-option></ps-select>',
    example: '<ps-option value="pink" selected>Pink</ps-option>',
    properties: p(
      ['value', 'Submitted value'],
      ['selected', 'Initial/current selection'],
      ['disabled', 'Prevents selection'],
    ),
    slots: p(['default', 'Option label']),
  }),
  entry({
    tag: 'ps-checkbox',
    title: 'Checkbox',
    category: 'Forms',
    description: 'A form-associated independent choice with a crisp custom mark.',
    demo: '<ps-checkbox name="extras" value="sparkles" checked>Extra sparkles</ps-checkbox>',
    example: '<ps-checkbox name="terms" required>I agree</ps-checkbox>',
    properties: [...form, ['checked', 'Selection state']],
    events: change,
    slots: p(['default', 'Label']),
    parts: p(['control', 'Checkbox'], ['label', 'Label']),
  }),
  entry({
    tag: 'ps-radio-group',
    title: 'Radio Group',
    category: 'Forms',
    description: 'One form value with roving focus and arrow-key selection.',
    demo: '<ps-radio-group name="era" value="1987" aria-label="Favorite era"><ps-radio value="1984">1984</ps-radio><ps-radio value="1987">1987</ps-radio><ps-radio value="1990">1990</ps-radio></ps-radio-group>',
    example:
      '<ps-radio-group name="size"><ps-radio value="s">Small</ps-radio><ps-radio value="l">Large</ps-radio></ps-radio-group>',
    properties: [...form, ['orientation', '`vertical` | `horizontal`']],
    events: change,
    slots: p(['default', 'ps-radio elements']),
    parts: p(['group', 'ARIA radio group']),
  }),
  entry({
    tag: 'ps-radio',
    title: 'Radio',
    category: 'Forms',
    description: 'An option hosted by ps-radio-group.',
    demo: '<ps-radio-group value="yes" aria-label="RSVP"><ps-radio value="yes">Yes, totally</ps-radio><ps-radio value="no">Not tonight</ps-radio></ps-radio-group>',
    example: '<ps-radio value="yes">Yes</ps-radio>',
    properties: p(
      ['value', 'Group value'],
      ['checked', 'Selection state'],
      ['disabled', 'Suppresses selection'],
    ),
    slots: p(['default', 'Label']),
    parts: p(['control', 'Radio circle'], ['label', 'Label button']),
  }),
  entry({
    tag: 'ps-switch',
    title: 'Switch',
    category: 'Forms',
    description: 'A form-associated immediate on/off setting.',
    demo: '<ps-switch name="neon" value="on" checked>Neon mode</ps-switch>',
    example: '<ps-switch name="notifications">Notifications</ps-switch>',
    properties: [...form, ['checked', 'On/off state']],
    events: change,
    slots: p(['default', 'Label']),
    parts: p(['control', 'Switch track'], ['label', 'Label']),
  }),
  entry({
    tag: 'ps-range',
    title: 'Range',
    category: 'Forms',
    description: 'A form-associated slider with a visible value readout.',
    demo: '<ps-range name="gloss" min="0" max="10" value="8" aria-label="Gloss level"></ps-range>',
    example: '<ps-range name="volume" min="0" max="100" value="50"></ps-range>',
    properties: [...form, ['min / max / step', 'Numeric range constraints']],
    events: p(['input', 'Composed live value'], ...change),
    parts: p(['input', 'Native range'], ['output', 'Current value']),
  }),

  entry({
    tag: 'ps-avatar',
    title: 'Avatar',
    category: 'Display',
    description: 'An image with resilient initials fallback.',
    demo: '<ps-avatar alt="Pink Sensation">PS</ps-avatar> <ps-avatar size="lg" alt="Starlight">SL</ps-avatar>',
    example: '<ps-avatar src="avatar.webp" alt="Jodie"></ps-avatar>',
    properties: p(['src', 'Image URL'], ['alt', 'Accessible alternative'], ...commonSize),
    slots: p(['default', 'Fallback initials']),
    parts: p(['image', 'Image'], ['fallback', 'Fallback']),
  }),
  entry({
    tag: 'ps-badge',
    title: 'Badge',
    category: 'Display',
    description: 'Compact status with semantic color variants.',
    demo: '<ps-badge>New</ps-badge> <ps-badge variant="success">Open</ps-badge> <ps-badge variant="warning">Low stock</ps-badge> <ps-badge variant="danger">Hot</ps-badge>',
    example: '<ps-badge variant="success">Ready</ps-badge>',
    properties: p(['variant', '`info` | `success` | `warning` | `danger`']),
    slots: p(['default', 'Status text']),
    parts: p(['base', 'Badge']),
  }),
  entry({
    tag: 'ps-chip',
    title: 'Chip',
    category: 'Display',
    description: 'A compact removable label.',
    demo: '<ps-chip removable><span slot="prefix">✦</span>Dream pop</ps-chip>',
    example: '<ps-chip removable>Selected filter</ps-chip>',
    properties: p(['removable', 'Shows remove action'], ['removeLabel', 'Remove action name']),
    events: p(['close', 'Requests removal']),
    slots: p(['default', 'Label'], ['prefix', 'Leading content']),
    parts: p(['base', 'Chip'], ['remove-button', 'Remove button']),
  }),
  entry({
    tag: 'ps-card',
    title: 'Card',
    category: 'Display',
    description: 'A raised sticker-like content surface.',
    demo: '<ps-card><span slot="header">Tonight at eight</span>Meet under the chrome palm trees.<div slot="footer"><ps-badge>All ages</ps-badge></div></ps-card>',
    example: '<ps-card><h3 slot="header">Title</h3>Content</ps-card>',
    slots: p(['header', 'Header'], ['default', 'Body'], ['footer', 'Footer']),
    parts: p(['base', 'Card'], ['header', 'Header'], ['body', 'Body'], ['footer', 'Footer']),
  }),
  entry({
    tag: 'ps-divider',
    title: 'Divider',
    category: 'Display',
    description: 'A semantic horizontal or vertical separator.',
    demo: '<span>Before</span><ps-divider></ps-divider><span>After</span>',
    example: '<ps-divider orientation="horizontal"></ps-divider>',
    properties: p(['orientation', '`horizontal` | `vertical`']),
    parts: p(['divider', 'Separator line']),
  }),
  entry({
    tag: 'ps-empty-state',
    title: 'Empty State',
    category: 'Display',
    description: 'A friendly next step when a collection is empty.',
    demo: '<ps-empty-state><span slot="illustration">♡</span><span slot="heading">Your wishlist is dreamy and empty</span>Add something spectacular.<ps-button slot="actions" size="sm">Browse looks</ps-button></ps-empty-state>',
    example:
      '<ps-empty-state><span slot="heading">No results</span>Try another filter.</ps-empty-state>',
    slots: p(
      ['illustration', 'Visual'],
      ['heading', 'Heading'],
      ['default', 'Description'],
      ['actions', 'Calls to action'],
    ),
    parts: p(
      ['base', 'Container'],
      ['illustration', 'Illustration'],
      ['heading', 'Heading'],
      ['description', 'Description'],
      ['actions', 'Actions'],
    ),
  }),
  entry({
    tag: 'ps-table',
    title: 'Table',
    category: 'Display',
    description: 'A responsive presentational shell around semantic native table markup.',
    demo: '<ps-table><table><caption>Arcade high scores</caption><thead><tr><th scope="col">Player</th><th scope="col">Score</th></tr></thead><tbody><tr><td>PIXEL</td><td>98,700</td></tr><tr><td>GLOSS</td><td>92,100</td></tr></tbody></table></ps-table>',
    example: '<ps-table><table>…native table markup…</table></ps-table>',
    slots: p(['default', 'Native table']),
    parts: p(['scroller', 'Overflow container']),
  }),

  entry({
    tag: 'ps-alert',
    title: 'Alert',
    category: 'Feedback',
    description: 'Persistent inline feedback with polite or assertive semantics.',
    demo: '<ps-alert variant="success" closable><span slot="heading">Saved!</span>Your look is ready for the runway.</ps-alert>',
    example:
      '<ps-alert variant="danger"><span slot="heading">Try again</span>Something went wrong.</ps-alert>',
    properties: p(['variant', 'Feedback severity'], ['closable', 'Shows close action']),
    events: p(['close', 'Fires before removal']),
    slots: p(['icon', 'Leading icon'], ['heading', 'Heading'], ['default', 'Message']),
    parts: p(['base', 'Alert'], ['heading', 'Heading'], ['close-button', 'Close button']),
  }),
  entry({
    tag: 'ps-progress',
    title: 'Progress',
    category: 'Feedback',
    description: 'Determinate and reduced-motion-safe indeterminate progress.',
    demo: '<ps-progress value="68" aria-label="Upload progress"></ps-progress><br><ps-progress indeterminate aria-label="Loading collection"></ps-progress>',
    example: '<ps-progress value="40" max="100"></ps-progress>',
    properties: p(
      ['value', 'Current progress'],
      ['max', 'Maximum'],
      ['indeterminate', 'Unknown duration'],
    ),
    parts: p(['track', 'Track'], ['indicator', 'Indicator']),
  }),
  entry({
    tag: 'ps-spinner',
    title: 'Spinner',
    category: 'Feedback',
    description: 'A compact status indicator that respects reduced motion.',
    demo: '<ps-spinner size="sm"></ps-spinner> <ps-spinner></ps-spinner> <ps-spinner size="lg"></ps-spinner>',
    example: '<ps-spinner aria-label="Saving"></ps-spinner>',
    properties: commonSize,
    parts: p(['spinner', 'Spinner ring']),
  }),
  entry({
    tag: 'ps-toast-stack',
    title: 'Toast Stack',
    category: 'Feedback',
    description: 'A polite fixed live region with an imperative push(options) API.',
    demo: '<ps-button size="sm" data-demo-toast>Push a toast</ps-button>',
    example:
      "<ps-toast-stack id=\"toasts\"></ps-toast-stack>\n<script>toasts.push({ message: 'Saved!', variant: 'success' })</script>",
    properties: p(['push(options)', 'Adds and returns a ps-toast']),
    slots: p(['default', 'ps-toast elements']),
    parts: p(['stack', 'Live region']),
  }),
  entry({
    tag: 'ps-toast',
    title: 'Toast',
    category: 'Feedback',
    description: 'A temporary notification managed alone or in a stack.',
    demo: '<ps-toast duration="0" variant="info"><span slot="title">Now playing</span>Electric Dreams</ps-toast>',
    example: '<ps-toast duration="5000">Changes saved.</ps-toast>',
    properties: p(
      ['variant', 'Feedback severity'],
      ['duration', 'Milliseconds; 0 persists'],
      ['closable', 'Shows close action'],
    ),
    events: p(['close', 'Fires before removal']),
    slots: p(['title', 'Title'], ['default', 'Message']),
    parts: p(['base', 'Toast'], ['title', 'Title'], ['close-button', 'Close button']),
  }),

  entry({
    tag: 'ps-navbar',
    title: 'Navbar',
    category: 'Navigation',
    description: 'A responsive navigation shell with a keyboard-operable mobile toggle.',
    demo: '<ps-navbar menu-label="Demo navigation"><strong slot="brand">PS</strong><a href="#catalog">Catalog</a><a href="#tokens">Tokens</a><ps-button slot="actions" size="sm">Join</ps-button></ps-navbar>',
    example: '<ps-navbar><a slot="brand" href="/">Brand</a><a href="/docs">Docs</a></ps-navbar>',
    properties: p(['open', 'Mobile navigation state'], ['menuLabel', 'Navigation name']),
    events: p(['toggle', 'Reports open state']),
    slots: p(['brand', 'Brand'], ['default', 'Links'], ['actions', 'Actions']),
    parts: p(
      ['nav', 'Navigation'],
      ['toggle', 'Mobile toggle'],
      ['links', 'Link region'],
      ['actions', 'Action region'],
    ),
  }),
  entry({
    tag: 'ps-breadcrumbs',
    title: 'Breadcrumbs',
    category: 'Navigation',
    description: 'A semantic breadcrumb navigation wrapper.',
    demo: '<ps-breadcrumbs><a href="#top">Home</a><span aria-hidden="true">›</span><a href="#catalog" aria-current="page">Components</a></ps-breadcrumbs>',
    example: '<ps-breadcrumbs><a href="/">Home</a> › <span>Current</span></ps-breadcrumbs>',
    properties: p(['accessibleLabel', 'Navigation name']),
    slots: p(['default', 'Breadcrumb content']),
    parts: p(['list', 'Breadcrumb row']),
  }),
  entry({
    tag: 'ps-tabs',
    title: 'Tabs',
    category: 'Navigation',
    description: 'Coordinated tabs with arrows, Home, End, and roving tabindex.',
    demo: '<ps-tabs value="hits" aria-label="Music"><ps-tab value="hits">Big hits</ps-tab><ps-tab value="mixes">Mixtapes</ps-tab><ps-tab-panel value="hits">The neon chart toppers.</ps-tab-panel><ps-tab-panel value="mixes">Hand-labeled favorites.</ps-tab-panel></ps-tabs>',
    example:
      '<ps-tabs value="one"><ps-tab value="one">One</ps-tab><ps-tab-panel value="one">Panel</ps-tab-panel></ps-tabs>',
    properties: p(['value', 'Selected tab value']),
    events: change,
    slots: p(['default', 'ps-tab and ps-tab-panel']),
    parts: p(['tablist', 'ARIA tablist'], ['panels', 'Panel region']),
  }),
  entry({
    tag: 'ps-tab',
    title: 'Tab',
    category: 'Navigation',
    description: 'A trigger managed by ps-tabs.',
    demo: '<ps-tabs value="pink"><ps-tab value="pink">Pink tab</ps-tab><ps-tab value="chrome">Chrome tab</ps-tab><ps-tab-panel value="pink">Bubblegum.</ps-tab-panel><ps-tab-panel value="chrome">Mirror shine.</ps-tab-panel></ps-tabs>',
    example: '<ps-tab value="details">Details</ps-tab>',
    properties: p(
      ['value', 'Selection key'],
      ['active', 'Selected state'],
      ['disabled', 'Suppresses selection'],
    ),
    slots: p(['default', 'Label']),
    parts: p(['tab', 'Native tab button']),
  }),
  entry({
    tag: 'ps-tab-panel',
    title: 'Tab Panel',
    category: 'Navigation',
    description: 'A focusable content panel managed by ps-tabs.',
    demo: '<ps-tabs value="panel"><ps-tab value="panel">Open panel</ps-tab><ps-tab-panel value="panel">Panel content with <a href="#catalog">a link</a>.</ps-tab-panel></ps-tabs>',
    example: '<ps-tab-panel value="details">Details content</ps-tab-panel>',
    properties: p(['value', 'Selection key'], ['active', 'Visible state']),
    slots: p(['default', 'Panel content']),
    parts: p(['panel', 'ARIA tabpanel']),
  }),
  entry({
    tag: 'ps-pagination',
    title: 'Pagination',
    category: 'Navigation',
    description: 'Compact page navigation with current-page semantics.',
    demo: '<ps-pagination page="3" count="6"></ps-pagination>',
    example: '<ps-pagination page="1" count="8"></ps-pagination>',
    properties: p(['page', 'Current one-based page'], ['count', 'Total pages']),
    events: p(['change', 'Detail contains page']),
    parts: p(['nav', 'Navigation'], ['button', 'Page buttons']),
  }),

  entry({
    tag: 'ps-accordion',
    title: 'Accordion',
    category: 'Disclosure & overlays',
    description: 'Coordinates one or many native disclosure items.',
    demo: '<ps-accordion><ps-accordion-item open><span slot="summary">Is it framework-neutral?</span>Completely.</ps-accordion-item><ps-accordion-item><span slot="summary">Does it sparkle?</span>Absolutely.</ps-accordion-item></ps-accordion>',
    example: '<ps-accordion multiple>…items…</ps-accordion>',
    properties: p(['multiple', 'Allows several open items']),
    slots: p(['default', 'ps-accordion-item elements']),
    parts: p(['base', 'Accordion stack']),
  }),
  entry({
    tag: 'ps-accordion-item',
    title: 'Accordion Item',
    category: 'Disclosure & overlays',
    description: 'A native details-based disclosure section.',
    demo: '<ps-accordion-item><span slot="summary">Care instructions</span>Polish chrome with a soft cloth.</ps-accordion-item>',
    example:
      '<ps-accordion-item open><span slot="summary">Question</span>Answer</ps-accordion-item>',
    properties: p(['open', 'Disclosure state'], ['disabled', 'Prevents opening']),
    events: p(['toggle', 'Detail contains open state']),
    slots: p(['summary', 'Trigger label'], ['default', 'Content']),
    parts: p(['summary', 'Summary'], ['content', 'Content']),
  }),
  entry({
    tag: 'ps-dialog',
    title: 'Dialog',
    category: 'Disclosure & overlays',
    description: 'A native modal with focus trapping, cancellation, and restoration.',
    demo: '<ps-button size="sm" data-open-dialog>Open dialog</ps-button><ps-dialog label="After-hours special"><span slot="heading">After-hours special</span>Two-for-one milkshakes until midnight.<ps-button slot="footer" data-close-dialog>Lovely</ps-button></ps-dialog>',
    example:
      '<ps-dialog id="dialog"><span slot="heading">Title</span>Body</ps-dialog>\n<script>dialog.showModal()</script>',
    properties: p(
      ['open', 'Modal state'],
      ['showModal()', 'Opens modally'],
      ['close(value)', 'Closes with return value'],
    ),
    events: p(
      ['cancel', 'Cancelable Escape/backdrop request'],
      ['close', 'Detail contains returnValue'],
    ),
    slots: p(['heading', 'Heading'], ['default', 'Body'], ['footer', 'Actions']),
    parts: p(
      ['dialog', 'Native dialog'],
      ['header', 'Header'],
      ['body', 'Body'],
      ['footer', 'Footer'],
      ['close-button', 'Close action'],
    ),
  }),
  entry({
    tag: 'ps-drawer',
    title: 'Drawer',
    category: 'Disclosure & overlays',
    description: 'A side sheet with a trapped focus loop and restored trigger focus.',
    demo: '<ps-button size="sm" data-open-drawer>Open drawer</ps-button><ps-drawer label="Shopping bag"><span slot="heading">Shopping bag</span>Your chrome jacket is waiting.<ps-button slot="footer" data-close-drawer>Done</ps-button></ps-drawer>',
    example: '<ps-drawer id="drawer" placement="end">…</ps-drawer>\n<script>drawer.show()</script>',
    properties: p(
      ['open', 'Panel state'],
      ['placement', '`start` | `end`'],
      ['show() / hide()', 'Public overlay methods'],
    ),
    events: p(['cancel', 'Cancelable dismissal request'], ['close', 'Panel closed']),
    slots: p(['heading', 'Heading'], ['default', 'Body'], ['footer', 'Actions']),
    parts: p(
      ['backdrop', 'Backdrop'],
      ['panel', 'Panel'],
      ['header', 'Header'],
      ['body', 'Body'],
      ['footer', 'Footer'],
    ),
  }),
  entry({
    tag: 'ps-dropdown',
    title: 'Dropdown',
    category: 'Disclosure & overlays',
    description: 'An anchored popup with outside-click and Escape dismissal.',
    demo: '<ps-dropdown><ps-button slot="trigger" size="sm" variant="outline">Open menu</ps-button><ps-menu><ps-menu-item value="save">Save look</ps-menu-item><ps-menu-item value="share">Share</ps-menu-item></ps-menu></ps-dropdown>',
    example: '<ps-dropdown><button slot="trigger">Open</button><ps-menu>…</ps-menu></ps-dropdown>',
    properties: p(
      ['open', 'Popup state'],
      ['placement', '`start` | `end`'],
      ['show() / hide()', 'Public popup methods'],
    ),
    events: p(['close', 'Popup closed']),
    slots: p(['trigger', 'Trigger'], ['default', 'Popup content']),
    parts: p(['popup', 'Popup surface']),
  }),
  entry({
    tag: 'ps-menu',
    title: 'Menu',
    category: 'Disclosure & overlays',
    description: 'A roving-focus command list with full arrow navigation.',
    demo: '<ps-menu aria-label="Look actions"><ps-menu-item value="wear"><span slot="prefix">✦</span>Wear now</ps-menu-item><ps-menu-item value="save">Save for later</ps-menu-item><ps-menu-item value="delete" disabled>Delete</ps-menu-item></ps-menu>',
    example: '<ps-menu><ps-menu-item value="edit">Edit</ps-menu-item></ps-menu>',
    properties: p(['accessibleLabel', 'Menu name']),
    events: change,
    slots: p(['default', 'ps-menu-item elements']),
    parts: p(['menu', 'ARIA menu']),
  }),
  entry({
    tag: 'ps-menu-item',
    title: 'Menu Item',
    category: 'Disclosure & overlays',
    description: 'A selectable command hosted by ps-menu.',
    demo: '<ps-menu><ps-menu-item value="sparkle">Add sparkle</ps-menu-item><ps-menu-item value="shine">Polish chrome</ps-menu-item></ps-menu>',
    example: '<ps-menu-item value="save">Save</ps-menu-item>',
    properties: p(['value', 'Command value'], ['disabled', 'Suppresses selection']),
    events: p(['change', 'Detail contains value']),
    slots: p(['default', 'Label'], ['prefix', 'Leading icon']),
    parts: p(['item', 'Native menu item button']),
  }),
  entry({
    tag: 'ps-tooltip',
    title: 'Tooltip',
    category: 'Disclosure & overlays',
    description: 'A concise pointer and keyboard-focus description.',
    demo: '<ps-tooltip><ps-icon-button label="Information">i</ps-icon-button><span slot="content">Meet me by the fountain</span></ps-tooltip>',
    example:
      '<ps-tooltip><button>Info</button><span slot="content">Helpful detail</span></ps-tooltip>',
    properties: p(['open', 'Visibility state'], ['placement', '`top` | `bottom`']),
    slots: p(['default', 'Trigger'], ['content', 'Description']),
    parts: p(['tooltip', 'Tooltip bubble']),
  }),
];

export const categories = [...new Set(catalog.map((item) => item.category))];
